<template>
  <div class="futuri-post-web-widget-v2-player">
    <v2-player-layout
      :dark-theme="darkTheme"
      :compact-on-mobile="mods.compactOnMobile"
    >
      <now-playing-section
        slot="top-half"
        :show-image-src="showImageSrc"
        :dark-theme="darkTheme"
        :show-title="showTitle"
        :active-episode="episodeCurrentlyPlaying"
        :episode-images="mods.episodeImages"
        :compact-on-mobile="mods.compactOnMobile"
        :station="station"
        :drop-to-live="dropToLive"
        :use-m4a="useM4a"
        @audio-player:ready="onAudioPlayerReady"
        @audio:pause="onActiveEpisodePaused"
        @audio:play="onActiveEpisodePlaying"
      >
      </now-playing-section>
      <playlist
        slot="bottom-half"
        :dark-theme="darkTheme"
        :episode-list="episodeList"
        :error-message="errorMessage"
        :active-episode-id="activeEpisodeId"
        :is-loading="isLoading"
        :is-active-episode-paused="isActiveEpisodePaused"
        @episode-click="onPlaylistEpisodeClick"
        @episode-pause-button="onPlaylistEpisodePauseButton"
        @episode-play-button="onPlaylistEpisodePlayButton"
      ></playlist>
    </v2-player-layout>
  </div>
</template>

<script>
import V2PlayerLayout from './V2PlayerLayout';
import NowPlayingSection from './NowPlayingSection';
import Playlist from './Playlist';

export default {
  name: 'v2-player',
  components: {
    V2PlayerLayout,
    NowPlayingSection,
    Playlist,
  },
  props: {
    darkTheme: Boolean,
    modsList: String,
    station: String,
    apiResourceUrl: {
      type: String,
      required: true,
    },
    dropToLive: Boolean,
    useM4a: Boolean,
  },
  data() {
    return {
      audioPlayer: null,
      showImageSrc: undefined,
      showTitle: undefined,
      isLoading: true,
      isActiveEpisodePaused: undefined, // TODO: rename to isActiveEpisodePlaying
      errorMessage: undefined,
      episodeList: [],
      activeEpisodeId: undefined,
    };
  },
  computed: {
    episodeCurrentlyPlaying() {
      return this.activeEpisodeId
        ? this.getEpisodeFromListById(this.activeEpisodeId)
        : undefined;
    },

    mods() {
      return {
        episodeImages:
          !!this.modsList &&
          this.modsList.split(',').includes('episode_images'),
        compactOnMobile:
          !!this.modsList &&
          this.modsList.split(',').includes('compact_on_mobile'),
      };
    },
  },
  methods: {
    retrieveShowData() {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', this.apiResourceUrl);
        // eslint-disable-next-line
        xhr.onreadystatechange = function () {
          if (this.readyState === 4) {
            if (this.status === 200) {
              resolve(JSON.parse(this.responseText));
            } else {
              reject(new Error(`${this.statusText} (${this.status})`));
            }
          }
        };
        xhr.send();
      });
    },

    processShowData({ show_image, show_title, playlists }) {
      this.isLoading = false;

      // eslint-disable-next-line
      this.showImageSrc = show_image;
      // eslint-disable-next-line
      this.showTitle = show_title;

      // An "episode" is known as a "playlist" on the server
      this.episodeList = playlists.map(
        ({
          id,
          name,
          description,
          image,
          m4a_file,
          timestamp_created,
          timestamp_updated,
        }) => ({
          id,
          name,
          description,
          image,
          m4a_file,
          // eslint-disable-next-line
          timestamp_created: new Date(timestamp_created * 1000),
          // eslint-disable-next-line
          timestamp_updated: new Date(timestamp_updated * 1000),
        })
      );

      // Set active episode to the first episode
      // if (this.episodeList.length > 0) {
      //  this.setActiveEpisode(this.episodeList[0].id);
      // }}
    },

    handleShowDataError(error) {
      this.isLoading = false;
      this.errorMessage = error.message;
    },

    getEpisodeFromListById(episodeId) {
      return this.episodeList.reduce((acc, episode) => {
        if (episode.id === episodeId) {
          return episode;
        }
        return acc;
      }, {});
    },

    setActiveEpisode(episodeId) {
      this.isActiveEpisodePaused = true;
      this.activeEpisodeId = episodeId;
    },

    onPlaylistEpisodeClick(episodeId) {
      if (this.activeEpisodeId !== episodeId) {
        this.setActiveEpisode(episodeId);
      } else {
        // If episode that was clicked is already the active episode, play/resume it.
        this.audioPlayer
          .play()
          .then(() => {
            this.isActiveEpisodePaused = false;
          })
          .catch((error) => {
            // eslint-disable-next-line
            console.error(error.message);
          });
      }
    },

    onPlaylistEpisodePauseButton() {
      this.isActiveEpisodePaused = true;
      this.audioPlayer.pause();
    },

    onPlaylistEpisodePlayButton(episodeId) {
      if (!this.activeEpisodeId) {
        // No active epsiodeId exists, set it as active.
        // This is neccessary when the player is launched without a default episodeId selected
        this.setActiveEpisode(episodeId);
        return;
      }

      if (this.activeEpisodeId === episodeId) {
        // TODO: Low priority: Clicking play twice will trigger playlist icone to show as playing,
        // even if audio is not playing yet yet
        // If the play button is clicked for the active episode, simply just play it.
        this.audioPlayer
          .play()
          .then(() => {
            this.isActiveEpisodePaused = false;
          })
          .catch((error) => {
            // eslint-disable-next-line
            console.error(error.message);
          });
      } else {
        // If a different episode's play button is clicked, set it as the active episode
        this.setActiveEpisode(episodeId);
      }
    },

    onAudioPlayerReady(audioPlayer) {
      this.audioPlayer = audioPlayer;
    },

    onActiveEpisodePaused() {
      // Updated by ViblastAudioPlayer
      this.isActiveEpisodePaused = true;
    },

    onActiveEpisodePlaying() {
      // Updated by ViblastAudioPlayer
      this.isActiveEpisodePaused = false;
    },
    onReady() {
      if (!!this.apiResourceUrl) {
        this.retrieveShowData()
          .then(this.processShowData)
          .catch(this.handleShowDataError);
      }
    },
  },
  ready() {
    this.onReady();
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/variables-v2';
.futuri-post-web-widget-v2-player {
}
</style>



// WEBPACK FOOTER //
// V2Player.vue?3f067c85